import { useEffect, useRef, useState } from 'react';
import { useSetError } from '../../redux/modals';
import { countConstraintChanges, getConstraintWarnings } from '../constraint-draft-utils';

interface DraftOptions<T> {
  identity: string;
  initialData: T | undefined;
  persist: (data: T) => Promise<T>;
  validate?: (data: T) => Promise<T>;
}

export const useConstraintDraft = <T extends { constraints: object }>({
  identity,
  initialData,
  persist,
  validate
}: DraftOptions<T>) => {
  const setError = useSetError();
  const [state, setState] = useState<{ identity: string; saved?: T; draft?: T }>({ identity });
  const saved = state.identity === identity ? (state.saved ?? initialData) : initialData;
  const draft = state.identity === identity ? (state.draft ?? saved) : saved;
  const changeCount =
    draft && saved ? countConstraintChanges(saved.constraints, draft.constraints) : 0;
  const [validation, setValidation] = useState<{ source: T; data?: T; error?: string }>();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!draft || !validate || validation?.source === draft) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      validate(draft).then(
        data => {
          if (!cancelled) {
            setValidation({ source: draft, data });
          }
        },
        error => {
          if (!cancelled) {
            setValidation({ source: draft, error: error.message });
          }
        }
      );
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draft, validate, validation]);

  const currentValidation = validation?.source === draft ? validation : undefined;
  const data = currentValidation?.data ?? draft;
  const warnings = getConstraintWarnings(data?.constraints);
  const canSave = Boolean(
    changeCount && (!validate || currentValidation?.data) && !warnings.length
  );

  return {
    data,
    changeCount,
    canSave,
    warnings,
    saving,
    validating: Boolean(draft && validate && !currentValidation),
    validationError: currentValidation?.error,
    retryValidation: () => setValidation(undefined),
    update: (next: T) => setState({ identity, saved, draft: next }),
    discard: () => setState({ identity, saved }),
    save: async () => {
      if (!data || !canSave || savingRef.current) return;
      savingRef.current = true;
      setSaving(true);
      try {
        const result = await persist(data);
        setState({ identity, saved: result });
      } catch (error) {
        setError({ message: `Couldn't save constraints: ${error.message}`, reportError: error });
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };
};
